import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * "2026-05-10" -> "10/05/2026".
 * Splits the string instead of going through Date: parsing a bare ISO date
 * yields UTC midnight, which renders as the previous day west of Greenwich.
 */
export function formatISODate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : String(iso);
}

/** Local calendar day as YYYY-MM-DD (toISOString would give the UTC day). */
export function todayISO(now = new Date()) {
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Chronological by the date of the booking itself, not by when it was
 * requested: upcoming first, so the top of every tab is what happens next.
 * Dates already served drop to the bottom, most recent first.
 * Returns a new array; the input is left alone.
 */
export function sortBookingsChronologically(bookings, today = todayISO()) {
  const key = (b) => `${b.date || ""} ${b.time || "00:00"}`;
  return [...bookings].sort((a, b) => {
    const aPast = (a.date || "") < today;
    const bPast = (b.date || "") < today;
    if (aPast !== bPast) return aPast ? 1 : -1;
    const cmp = key(a).localeCompare(key(b));
    return aPast ? -cmp : cmp;
  });
}

export const BOOKING_TABS = ["all", "pending", "awaiting_payment", "paid", "confirmed", "cancelled", "rejected"];

export function matchesTab(b, tab) {
  if (tab === "all") return true;
  if (tab === "paid") return b.payment_status === "paid";
  if (tab === "awaiting_payment") return b.status === "confirmed" && b.payment_status === "pending";
  return b.status === tab;
}

export const SORT_MODES = ["upcoming", "date_asc", "date_desc", "created_desc", "created_asc", "name_asc", "guests_desc"];

export function sortBookings(bookings, mode = "upcoming", today = todayISO()) {
  if (mode === "upcoming") return sortBookingsChronologically(bookings, today);
  const slot = (b) => `${b.date || ""} ${b.time || "00:00"}`;
  const name = (b) => `${b.nome || ""} ${b.cognome || ""}`.trim().toLowerCase();
  const cmp = {
    date_asc: (a, b) => slot(a).localeCompare(slot(b)),
    date_desc: (a, b) => slot(b).localeCompare(slot(a)),
    created_desc: (a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""),
    created_asc: (a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""),
    name_asc: (a, b) => name(a).localeCompare(name(b), "it"),
    guests_desc: (a, b) => (b.guests || 0) - (a.guests || 0) || slot(a).localeCompare(slot(b)),
  }[mode];
  return cmp ? [...bookings].sort(cmp) : [...bookings];
}

/** Milliseconds left until an ISO deadline, never negative. */
export function msUntil(deadlineIso, now = Date.now()) {
  if (!deadlineIso) return null;
  const t = Date.parse(deadlineIso);
  return Number.isFinite(t) ? Math.max(0, t - now) : null;
}

export function formatCountdown(ms) {
  const total = Math.ceil(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
